import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import SupabaseUserService from '../services/supabaseUserService';
import { OAUTH_AUTO_REDIRECT, OAUTH_REDIRECT_KEY } from './authConstants';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<boolean>;
  signUp: (email: string, password: string, fullName: string) => Promise<User | null>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  checkAdminRole: () => Promise<boolean>;
  signInWithGoogle: (redirectPath?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const userService = SupabaseUserService.getInstance();
   
  const _restoredFromLocalRef = useRef(false);

  const checkAdminRole = useCallback(async (currentUser: User | null = user): Promise<boolean> => {
    logger.debug('Verificando role de admin...');
    try {
      const userToCheck = currentUser || user;

      if (!userToCheck) {
        logger.info('❌ Sem usuário ativo para verificar a role');
        setIsAdmin(false);
        return false;
      }
      
      logger.sensitive('Buscando perfil do usuário', { userId: userToCheck.id });
      const profile = await userService.getUserProfile(userToCheck.id);
      logger.debug('Perfil encontrado:', profile ? { role: profile.role } : 'Perfil não encontrado');
      
      const adminRole = profile?.role === 'admin';
      logger.info(adminRole ? '✅ Usuário é admin' : '❌ Usuário não é admin');
      
      setIsAdmin(adminRole);
      return adminRole;
    } catch (error) {
      console.error('❌ Erro ao verificar role de admin:', error);
      setIsAdmin(false);
      return false;
    }
  }, [user, userService]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Escutar mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_IN' && session?.access_token && session?.refresh_token) {
        try {
          localStorage.setItem('supabase.auth.token', JSON.stringify({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_at: session.expires_at,
            expires_in: session.expires_in,
            token_type: session.token_type
          }));
          logger.debug('[AuthContext] Tokens sincronizados no localStorage via onAuthStateChange');
        } catch (error) {
          logger.warn('[AuthContext] Falha ao sincronizar tokens no localStorage após SIGNED_IN', error);
        }
      }

      // Criar ou atualizar perfil quando usuário faz login
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          const existingProfile = await userService.getUserProfile(session.user.id);
          
          if (!existingProfile) {
            // Criar perfil se não existir
            await userService.createUserProfile(session.user.id, {
              name: session.user.user_metadata?.full_name || session.user.email || '',
              email: session.user.email || ''
            });
          }
          
          // Verificar role de admin
          await checkAdminRole();
        } catch (error) {
          // Ignorar erros de JWT expirado durante refresh automático do Supabase
          const isJWTError = error instanceof Error && error.message.includes('JWT expired');
          if (!isJWTError) {
            logger.error('Erro ao gerenciar perfil do usuário:', error);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkAdminRole, userService]);

  const signIn = async (email: string, password: string) => {
    logger.debug('[AuthContext] Tentando fazer login');
    logger.sensitive('[AuthContext] Email', { email });

    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      throw new Error('Configuração do Supabase não encontrada. Verifique o arquivo .env');
    }

    try {
      logger.debug('[AuthContext] Usando API REST (fetch) para signIn...');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [AuthContext] Erro no login via API REST:', errorData);
        throw new Error(errorData.error_description || 'E-mail ou senha inválidos');
      }

      const data = await response.json();
      logger.success('[AuthContext] Login via API REST bem-sucedido');

      // Persistir tokens imediatamente para que serviços REST tenham Authorization durante onAuthStateChange
      try {
        localStorage.setItem('supabase.auth.token', JSON.stringify({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at,
          expires_in: data.expires_in,
          token_type: data.token_type
        }));
        console.log('💾 [AuthContext] Tokens salvos no localStorage (pré-setSession)');
      } catch (e) {
        console.warn('⚠️ [AuthContext] Falha ao salvar tokens pré-setSession:', e);
      }

      // Persistir sessão no SDK de forma não-bloqueante para evitar travas
      try {
        void supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token
        }).then(({ data: s, error }) => {
          if (error) {
            console.warn('⚠️ [AuthContext] setSession retornou erro (não-bloqueante):', error)
            return
          }
          console.log('✅ [AuthContext] Sessão estabelecida com sucesso (async)')
          if (s?.session?.user) {
            setSession(s.session)
            setUser(s.session.user)
          }
        }).catch((e) => {
          console.warn('⚠️ [AuthContext] setSession falhou (async):', e)
        })
      } catch (e) {
        console.warn('⚠️ [AuthContext] setSession não pôde ser iniciado:', e)
      }
      // Atualizar estado local imediatamente (fallback) para liberar a UI
      if (data?.user) {
        setUser(data.user)
      }

      // Atualizar tokens (garantia) após setSession (não-bloqueante)
      try {
        localStorage.setItem('supabase.auth.token', JSON.stringify({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at,
          expires_in: data.expires_in,
          token_type: data.token_type
        }));
        logger.debug('[AuthContext] Tokens (pós-setSession) atualizados no localStorage');
      } catch {
        // Ignore errors when updating tokens in localStorage
      }

      logger.success('[AuthContext] Login realizado com sucesso');
      logger.sensitive('[AuthContext] Usuário autenticado', { userId: data.user?.id });

      // Verificar role de admin em segundo plano para não bloquear o fluxo de login
      try { void checkAdminRole(data.user); } catch { /* ignore */ }
      return true;

    } catch (err) {
      console.error('❌ [AuthContext] Exceção capturada no signIn:', err);
      if (err instanceof Error && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))) {
        throw new Error('Erro de conexão: Verifique sua internet ou a URL do Supabase.');
      }
      throw err;
    }
  };

  const signUp = async (email: string, password: string, fullName: string): Promise<User | null> => {
    logger.debug('AuthContext.signUp chamado');
    logger.sensitive('Signup Email', { email });
    logger.sensitive('Signup Nome', { fullName });

    const redirectUrl = `${window.location.origin}/signup/confirmed`;

    try {
      logger.debug('[AuthContext] Executando supabase.auth.signUp com emailRedirectTo', { redirectUrl });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            source: 'web',
            signup_date: new Date().toISOString(),
          },
        },
      });

      if (error) {
        logger.error('[AuthContext] Erro ao criar usuário:', error);
        throw error;
      }

      logger.success('[AuthContext] Cadastro enviado; aguardando confirmação de email');

      // Retorna o user (se disponível) mas NÃO faz login automático
      return data.user ?? null;
    } catch (err) {
      console.error('❌ Exceção capturada no signUp:', err);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      console.log('🚪 [AuthContext] Iniciando logout...');
      
      // Limpar todos os dados do localStorage e sessionStorage relacionados ao app
      console.log('🧹 [AuthContext] Limpando localStorage e sessionStorage...');
      localStorage.removeItem('magistral_fact_notifications');
      localStorage.removeItem('homeopathy_quiz_data');
      localStorage.removeItem('shown_badge_notifications');
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem(OAUTH_REDIRECT_KEY);
      
      // Limpar cache de análise de prescrição
      sessionStorage.removeItem('magistral_prescription_analysis');
      console.log('🧹 [AuthContext] Cache de prescrição limpo');
      // Limpar apenas as chaves do tutorial do usuário atual (namespaced) + legado
      try {
        const uid = user?.id;
        if (uid) {
          const prefix = 'tutorial:' + uid + ':';
          const toRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i) || '';
            if (k.startsWith(prefix)) toRemove.push(k);
          }
          toRemove.forEach((k) => localStorage.removeItem(k));
        }
        // legado global de supressão
        localStorage.removeItem('tutorial:suppressed');
      } catch { /* ignore */ }

      
      // Fazer logout no Supabase
      console.log('🔐 [AuthContext] Fazendo logout no Supabase...');
      const { error } = await supabase.auth.signOut();
      
      // Ignorar erro de sessão ausente (já deslogado)
      if (error && error.message !== 'Auth session missing!') {
        console.error('❌ [AuthContext] Erro ao fazer logout:', error);
        // Não lançar erro, apenas logar - continuar com limpeza local
      } else if (error) {
        console.log('ℹ️ [AuthContext] Sessão já estava ausente, continuando limpeza...');
      }
      
      // Limpar estado local (sempre executar, mesmo com erro)
      setUser(null);
      setIsAdmin(false);
      
      console.log('✅ [AuthContext] Logout realizado com sucesso!');
    } catch (error) {
      console.error('❌ [AuthContext] Erro no processo de logout:', error);
      // Mesmo com erro, limpar estado local
      setUser(null);
      setIsAdmin(false);
      localStorage.removeItem(OAUTH_REDIRECT_KEY);
      localStorage.removeItem('supabase.auth.token');
      // Não re-lançar erro para não bloquear o logout
    }
  };

  const deleteAccount = async () => {
    console.log('🗑️ [AuthContext] Iniciando exclusão de conta...');
    
    if (!user) {
      throw new Error('Nenhum usuário autenticado');
    }

    try {
      // Deletar usuário do Auth usando a função RPC
      // O CASCADE irá automaticamente deletar todos os dados relacionados:
      // - profiles
      // - families (criadas pelo usuário)
      // - family_members (vinculados ao usuário)
      // - notifications
      // - member_points_history / family_points_history
      // - quiz_attempts
      // - quiz_answer_attempts
      // - user_fact_reads
      // E todos os dados em cascata de family_members:
      // - medications, medication_logs
      // - health_events
      // - medical_records
      // - member_points (v2)
      // - reminders
      // - streak_achievements
      logger.debug('[AuthContext] Deletando usuário e todos os dados relacionados...');
      
      const { error: authError } = await supabase.rpc('delete_user_account', {});

      if (authError) {
        console.error('❌ [AuthContext] Erro ao deletar conta:', authError);
        throw authError;
      }

      console.log('✅ [AuthContext] Usuário e todos os dados deletados com sucesso!');

      // Fazer logout
      await signOut();
      
      console.log('✅ [AuthContext] Conta excluída com sucesso!');
    } catch (error) {
      console.error('❌ [AuthContext] Erro ao excluir conta:', error);
      throw error;
    }
  };

  const signInWithGoogle = async (redirectPath?: string) => {
    console.log('🔐 [AuthContext] Iniciando login com Google...');

    try {
      // Limpar sessão anterior antes de iniciar OAuth (previne conflito de sessões)
      console.log('🧹 [AuthContext] Limpando sessão anterior antes do OAuth...');
      try {
        await supabase.auth.signOut();
        localStorage.removeItem('supabase.auth.token');
        setSession(null);
        setUser(null);
      } catch (cleanupErr) {
        console.warn('⚠️ [AuthContext] Erro ao limpar sessão anterior:', cleanupErr);
      }

      const targetRedirect = redirectPath ?? OAUTH_AUTO_REDIRECT;
      localStorage.setItem(OAUTH_REDIRECT_KEY, targetRedirect);

      // Normalizar origin para evitar redirect inválido (ex.: dev localhost:3000 ou IP local no mobile)
      const currentOrigin = window.location.origin;
      const PROD = 'https://magistralhomeopatia.com.br';
      const DEV = 'http://localhost:5173'; // único localhost permitido no Supabase
      const normalizedOrigin = (() => {
        try {
          // Produção (com ou sem www)
          if (/^https?:\/\/(www\.)?magistralhomeopatia\.com\.br$/i.test(currentOrigin)) {
            return PROD;
          }
          // Ambiente de desenvolvimento suportado
          if (currentOrigin === DEV) {
            return DEV;
          }
          // Qualquer outro origin (localhost:3000, 127.x, 192.168.x.x, IP público, etc.)
          // deve usar o domínio de produção cadastrado no Supabase para o callback.
          console.log('🌐 [AuthContext] Origin não homologado para OAuth:', currentOrigin, '→ usando PROD para redirect');
          return PROD;
        } catch {
          return PROD;
        }
      })();
      const redirectTo = `${normalizedOrigin}/auth/callback`;
      console.log('🔗 [AuthContext] OAuth redirectTo:', redirectTo);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo
        }
      });

      if (error) {
        console.error('❌ [AuthContext] Erro no login com Google:', error);
        throw new Error(error.message || 'Não foi possível autenticar com Google.');
      }
      console.log('✅ [AuthContext] Redirecionamento para Google iniciado');
    } catch (err) {
      console.error('❌ [AuthContext] Exceção no login com Google:', err);
      throw err;
    }
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    signIn,
    signUp,
    signOut,
    deleteAccount,
    checkAdminRole,
    signInWithGoogle
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}